import { Button } from '@/components/ui/button';
import { Download, FileDown, FileText, Building2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { axiosClient } from '@/lib/axiosClient';
import toast from 'react-hot-toast';
import { useState, useEffect } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

interface ExportButtonsProps {
  periodId: number;
  isFab?: boolean;
  institutions?: { institution: string; total: number }[];
}

interface ExportOptions {
  packages: { package_id: string; name: string }[];
  guest_categories: { id: string; name: string }[];
}

export function ExportButtons({ periodId, isFab, institutions = [] }: ExportButtonsProps) {
  const [isOpen, setIsOpen] = useState(false);
  
  // Export Options Data
  const [exportOptions, setExportOptions] = useState<ExportOptions>({ packages: [], guest_categories: [] });
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);

  // Form State
  const [institution, setInstitution] = useState('all');
  const [paymentStatus, setPaymentStatus] = useState('all');
  const [isPrinted, setIsPrinted] = useState('all');
  const [isRfid, setIsRfid] = useState('all');
  const [format, setFormat] = useState('excel');

  // Checkboxes State
  const [includeDashboard, setIncludeDashboard] = useState(true);
  const [includeParticipants, setIncludeParticipants] = useState(true);
  const [combineParticipants, setCombineParticipants] = useState(true);
  const [selectedPackages, setSelectedPackages] = useState<string[]>([]);
  const [includeGuests, setIncludeGuests] = useState(true);
  const [selectedGuests, setSelectedGuests] = useState<string[]>([]);
  const [showStatusInfo, setShowStatusInfo] = useState(true);
  
  // Filter out any empty/invalid institution names for the dropdown
  const validInstitutions = institutions.filter(i => i.institution && i.institution !== 'Tidak Diketahui');

  // Fetch options when dialog opens
  useEffect(() => {
    if (isOpen) {
      const fetchOptions = async () => {
        setIsLoadingOptions(true);
        try {
          const { data } = await axiosClient.get(`/periods/${periodId}/convention-report/export-options`);
          setExportOptions(data);
          // Auto-select all by default
          setSelectedPackages(data.packages.map((p: any) => p.package_id));
          setSelectedGuests([...data.guest_categories.map((c: any) => c.id), 'uncategorized']);
        } catch (error) {
          toast.error('Gagal mengambil opsi data');
        } finally {
          setIsLoadingOptions(false);
        }
      };
      fetchOptions();
    }
  }, [isOpen, periodId]);

  const handleExport = async () => {
    if (format === 'excel' && !includeDashboard && (!includeParticipants || (includeParticipants && !combineParticipants && selectedPackages.length === 0)) && (!includeGuests || (includeGuests && selectedGuests.length === 0))) {
      return toast.error('Pilih setidaknya satu data yang ingin diekspor.');
    }

    try {
      toast.loading(`Mempersiapkan file...`, { id: 'export' });
      
      let endpoint = `/periods/${periodId}/convention-report/`;
      endpoint += format === 'pdf' ? 'export-pdf' : 'export-dynamic';
      
      const params = new URLSearchParams();
      if (institution !== 'all') params.append('institution', institution);
      if (paymentStatus !== 'all') params.append('payment_status', paymentStatus);
      if (isPrinted !== 'all') params.append('is_printed', isPrinted);
      if (isRfid !== 'all') params.append('is_rfid', isRfid);

      if (format === 'excel') {
        params.append('include_dashboard', includeDashboard ? '1' : '0');
        params.append('combine_participants', (includeParticipants && combineParticipants) ? '1' : '0');
        params.append('packages', includeParticipants ? selectedPackages.join(',') : '');
        params.append('guest_categories', includeGuests ? selectedGuests.join(',') : '');
      }
      
      params.append('show_status_info', showStatusInfo ? '1' : '0');

      const queryString = params.toString();
      if (queryString) {
        endpoint += `?${queryString}`;
      }

      const response = await axiosClient.get(endpoint, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const now = new Date();
      const pad = (n: number) => n.toString().padStart(2, '0');
      const timestamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
      
      let filename = `Rekap_Konvensi_${timestamp}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      
      // Filename from content-disposition header if available, fallback otherwise
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch && filenameMatch.length === 2) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Unduhan berhasil', { id: 'export' });
      setIsOpen(false);
    } catch (error) {
      toast.error('Gagal mengunduh file', { id: 'export' });
      console.error('Export error:', error);
    }
  };

  const togglePackage = (id: string) => {
    setSelectedPackages(prev => 
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const toggleGuest = (id: string) => {
    setSelectedGuests(prev => 
      prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    setIncludeDashboard(true);
    setIncludeParticipants(true);
    setCombineParticipants(false);
    setSelectedPackages(exportOptions.packages.map(p => p.package_id));
    setIncludeGuests(true);
    setSelectedGuests([...exportOptions.guest_categories.map(c => c.id.toString()), 'uncategorized']);
  };

  const unselectAll = () => {
    setIncludeDashboard(false);
    setIncludeParticipants(false);
    setCombineParticipants(false);
    setSelectedPackages([]);
    setIncludeGuests(false);
    setSelectedGuests([]);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {isFab ? (
          <Button size="icon" className="h-14 w-14 rounded-full shadow-2xl bg-blue-600 hover:bg-blue-700 text-white">
            <Download className="h-6 w-6" />
          </Button>
        ) : (
          <Button variant="default" className="h-11 px-4 bg-blue-600 hover:bg-blue-700 text-white shadow-md">
            <Download className="mr-2 h-4 w-4" />
            Export Laporan
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Kustomisasi Export Laporan</DialogTitle>
          <DialogDescription>
            Pilih jenis data dan filter yang ingin Anda ekspor.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-4 -mr-4 min-h-0">
          <div className="grid gap-5 py-4 px-1">
            
            {/* Format File */}
            <div className="space-y-3">
              <Label className="text-slate-700 font-semibold">1. Format File</Label>
              <Select value={format} onValueChange={setFormat}>
                <SelectTrigger>
                  <div className="flex items-center gap-2">
                    {format === 'excel' ? <FileDown className="h-4 w-4 text-emerald-600" /> : <FileText className="h-4 w-4 text-rose-600" />}
                    <span className="truncate">{format === 'excel' ? 'Excel (.xlsx)' : 'PDF Report (.pdf)'}</span>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="excel">
                    <div className="flex items-center gap-2">
                      <FileDown className="h-4 w-4 text-emerald-600" />
                      <span>Excel (.xlsx) - Dinamis Multi Sheet</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="pdf">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-rose-600" />
                      <span>PDF Report (.pdf) - Standar Laporan</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Opsi Tampilan */}
            <div className="space-y-3 bg-slate-50 dark:bg-slate-900/50 p-4 rounded-lg border border-slate-100 dark:border-slate-800">
              <Label className="text-slate-700 font-semibold">Opsi Tampilan (Berlaku untuk Excel & PDF)</Label>
              <div className="flex items-start space-x-3 pt-2">
                <Checkbox 
                  id="showStatusInfo" 
                  checked={showStatusInfo} 
                  onCheckedChange={(c) => setShowStatusInfo(c as boolean)} 
                  className="mt-1"
                />
                <div className="space-y-1">
                  <label htmlFor="showStatusInfo" className="text-sm font-medium leading-none cursor-pointer">
                    Tampilkan Kolom Status Cetak Kartu & Status RFID
                  </label>
                  <p className="text-xs text-slate-500">
                    Jika tidak dicentang, informasi mengenai kartu sudah tercetak atau RFID terscan akan disembunyikan.
                  </p>
                </div>
              </div>
            </div>

            {/* Data Selection (Hanya Excel) */}
            {format === 'excel' && (
              <div className="space-y-3 bg-slate-50 dark:bg-slate-900/50 p-4 rounded-lg border border-slate-100 dark:border-slate-800">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Label className="text-slate-700 font-semibold">2. Data yang Ingin Diekspor</Label>
                  {exportOptions.packages.length > 0 && (
                    <div className="flex space-x-2">
                      <Button type="button" variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={selectAll}>Pilih Semua</Button>
                      <Button type="button" variant="outline" size="sm" className="h-7 px-2 text-xs text-red-600 hover:text-red-700" onClick={unselectAll}>Hapus Pilihan</Button>
                    </div>
                  )}
                </div>
                {isLoadingOptions ? (
                  <p className="text-sm text-slate-500">Memuat opsi data...</p>
                ) : (
                  <div className="space-y-4 mt-2">
                    {/* Dashboard */}
                    <div className="flex items-center space-x-2 pb-2 border-b">
                      <Checkbox id="includeDashboard" checked={includeDashboard} onCheckedChange={(c) => setIncludeDashboard(c as boolean)} />
                      <label htmlFor="includeDashboard" className="text-sm font-medium leading-none cursor-pointer">
                        Lembar Rekapitulasi / Dashboard
                      </label>
                    </div>

                    {/* Peserta */}
                    <div className="space-y-3 border-b pb-4">
                      <div className="flex items-center space-x-2 bg-slate-100 dark:bg-slate-800 p-2 rounded">
                        <Checkbox 
                          id="includeParticipants" 
                          checked={includeParticipants} 
                          onCheckedChange={(c) => setIncludeParticipants(c as boolean)} 
                        />
                        <label htmlFor="includeParticipants" className="text-sm font-bold uppercase tracking-wider leading-none cursor-pointer text-slate-700 dark:text-slate-200">
                          Data Peserta
                        </label>
                      </div>
                      
                      {includeParticipants && (
                        <div className="pl-6 space-y-3">
                          <div className="flex items-center space-x-2 bg-blue-50 dark:bg-blue-900/20 p-2 rounded border border-blue-100 dark:border-blue-800/50">
                            <Checkbox 
                              id="combineParticipants" 
                              checked={combineParticipants} 
                              onCheckedChange={(c) => setCombineParticipants(c as boolean)} 
                            />
                            <label htmlFor="combineParticipants" className="text-sm font-medium leading-none cursor-pointer text-blue-700 dark:text-blue-300">
                              Jadikan 1 Sheet (Semua Peserta)
                            </label>
                          </div>

                          {!combineParticipants && (
                            <div className="pl-2 grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                              {exportOptions.packages.map(pkg => (
                                <div key={pkg.package_id} className="flex items-center space-x-2">
                                  <Checkbox 
                                    id={`pkg-${pkg.package_id}`} 
                                    checked={selectedPackages.includes(pkg.package_id)} 
                                    onCheckedChange={() => togglePackage(pkg.package_id)} 
                                  />
                                  <label htmlFor={`pkg-${pkg.package_id}`} className="text-sm leading-none cursor-pointer truncate text-slate-600 dark:text-slate-300">
                                    {pkg.name}
                                  </label>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Tamu / Panitia */}
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2 bg-slate-100 dark:bg-slate-800 p-2 rounded">
                        <Checkbox 
                          id="includeGuests" 
                          checked={includeGuests} 
                          onCheckedChange={(c) => setIncludeGuests(c as boolean)} 
                        />
                        <label htmlFor="includeGuests" className="text-sm font-bold uppercase tracking-wider leading-none cursor-pointer text-slate-700 dark:text-slate-200">
                          Kategori Tamu & Panitia
                        </label>
                      </div>

                      {includeGuests && (
                        <div className="pl-6 grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                          {exportOptions.guest_categories.map(cat => (
                          <div key={cat.id} className="flex items-center space-x-2">
                            <Checkbox 
                              id={`guest-${cat.id}`} 
                              checked={selectedGuests.includes(cat.id.toString())} 
                              onCheckedChange={() => toggleGuest(cat.id.toString())} 
                            />
                            <label htmlFor={`guest-${cat.id}`} className="text-sm leading-none cursor-pointer truncate">
                              {cat.name}
                            </label>
                          </div>
                        ))}
                          <div className="flex items-center space-x-2">
                            <Checkbox 
                              id="guest-uncategorized" 
                              checked={selectedGuests.includes('uncategorized')} 
                              onCheckedChange={() => toggleGuest('uncategorized')} 
                            />
                            <label htmlFor="guest-uncategorized" className="text-sm leading-none cursor-pointer truncate">
                              Tanpa Kategori
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Filter Institusi */}
            <div className="space-y-3">
              <Label className="text-slate-700 font-semibold">{format === 'excel' ? '3' : '2'}. Filter Institusi (Opsional)</Label>
              <Select value={institution} onValueChange={setInstitution}>
                <SelectTrigger>
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-slate-400" />
                    <SelectValue placeholder="Semua Institusi" />
                  </div>
                </SelectTrigger>
                <SelectContent className="max-h-[200px]">
                  <SelectItem value="all">Semua Institusi</SelectItem>
                  {validInstitutions.map((inst, idx) => (
                    <SelectItem key={idx} value={inst.institution}>
                      {inst.institution} ({inst.total})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Specific Filters */}
            <div className="space-y-3 bg-slate-50 dark:bg-slate-900/50 p-4 rounded-lg border border-slate-100 dark:border-slate-800">
              <Label className="text-slate-700 font-semibold">{format === 'excel' ? '4' : '3'}. Filter Tambahan</Label>
              
              <div className="space-y-4 mt-2">
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Status Pembayaran Peserta:</Label>
                  <Select value={paymentStatus} onValueChange={setPaymentStatus}>
                    <SelectTrigger className="h-9 text-sm bg-white dark:bg-slate-950">
                      <SelectValue placeholder="Semua Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Semua Status</SelectItem>
                      <SelectItem value="paid">Hanya Lunas</SelectItem>
                      <SelectItem value="pending">Menunggu Konfirmasi</SelectItem>
                      <SelectItem value="unpaid">Belum Bayar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Status Cetak ID Card (Semua Kategori):</Label>
                  <Select value={isPrinted} onValueChange={setIsPrinted}>
                    <SelectTrigger className="h-9 text-sm bg-white dark:bg-slate-950">
                      <SelectValue placeholder="Semua Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Semua Status Cetak</SelectItem>
                      <SelectItem value="1">Sudah Dicetak Saja</SelectItem>
                      <SelectItem value="0">Belum Dicetak Saja</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Status Data RFID (Semua Kategori):</Label>
                  <Select value={isRfid} onValueChange={setIsRfid}>
                    <SelectTrigger className="h-9 text-sm bg-white dark:bg-slate-950">
                      <SelectValue placeholder="Semua Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Semua Status RFID</SelectItem>
                      <SelectItem value="1">Sudah Terscan Saja</SelectItem>
                      <SelectItem value="0">Belum Terscan Saja</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

          </div>
        </div>

        <div className="flex justify-end pt-4 mt-2 border-t dark:border-slate-800">
          <Button onClick={handleExport} className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white">
            <Download className="mr-2 h-4 w-4" /> Unduh Laporan
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
